import React, { useState, useEffect } from "react";
import AuthSwitch from "@/components/AuthSwitch";
import GitHub from "@/assets/Github.svg";
import { login, register } from "@/services/auth";
import { useNavigate } from "react-router-dom";
import { profilePictures } from "@/lib/data";
import useUserData from "@/hooks/useUserData";
import { API_URL } from "@/env";
import EyeClosed from "@/assets/icons/EyeClosed.svg";
import Eye from "@/assets/icons/Eye.svg";
import LoadingBlack from "@/assets/icons/LoadingBlack.svg";
import { motion, AnimatePresence } from "motion/react";

const validateEmail = (email: string) => {
  // Very basic email validation. Adjust as needed.
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email.toLowerCase());
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const { setUser } = useUserData();

  // Login form
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Basic validations
    if (!newPassword) {
      setError("New password is required.");
      return;
    }
    if (!confirmNewPassword) {
      setError("Please confirm new password.");
      return;
    }

    if (confirmNewPassword !== newPassword) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          newPassword: newPassword,
          token: resetToken,
        }),
      });

      const data = await res.json();

      // console.log({ data }, data.status);

      if (data.status === "success" && data.email) {
        setError("");
        setMessage(data.message);
        setTimeout(async () => {
          const loginData = await login(data.email, newPassword);

          if (loginData.user) {
            setUser(data.user);
          }
          if (loginData.access_token) {
            navigate("/dashboard");
          }
        }, 1500);
      } else {
        setError(data.error);
        setMessage("");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (token) {
      setResetToken(token);
      // console.log("Detected reset code:", token);
      // Optionally: auto-fetch token or show password reset fields here
    }
  }, [location.search]);

  return (
    <form
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
        }
      }}
      onSubmit={handleSubmit}
      className="w-[400px] text-white bg-black/[0.8] rounded-[35px] backdrop-blur-[40px] p-[1px]"
    >
      <div className="border-[0.5px] border-white/[0.35] rounded-[34px] w-full h-full pt-[30px] px-[20px] pb-[25px]">
        <div className="w-[250px] text-center mx-auto">
          <p className="onest-smb text-[20px] leading-[25.5px]">
            Reset Your Password
          </p>
          <p className="onest-md text-[14px] leading-[21px] mt-[6px] text-white/[0.35]">
            Please enter your new password below.
          </p>
        </div>

        <div className="mt-[25px]">
          <>
            <div className="flex flex-col gap-[6px] w-full mt-[12px]">
              <label
                className="text-[14px] onest-md leading-[17.85px] text-white/[0.35] pl-[15px]"
                htmlFor="new_password"
              >
                Enter new password
              </label>
              <div className="relative w-full">
                <input
                  id="new_password"
                  type={showNewPassword ? "text" : "password"}
                  className="w-full px-[15px] onest-md h-[37px] bg-white/[0.05] text-white rounded-[15px] border-[0.5px] border-white/[0.1]"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />

                <AnimatePresence>
                  {newPassword && (
                    <motion.button
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowNewPassword((prev) => !prev);
                      }}
                      className="absolute top-[9.5px] right-[15px]"
                    >
                      <img src={showNewPassword ? Eye : EyeClosed} />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="flex flex-col gap-[6px] w-full mt-[12px]">
              <label
                className="text-[14px] onest-md leading-[17.85px] text-white/[0.35] pl-[15px]"
                htmlFor="confirm_password"
              >
                Confirm new password
              </label>
              <div className="relative w-full">
                <AnimatePresence>
                  {confirmNewPassword && (
                    <motion.button
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowConfirmPassword((prev) => !prev);
                      }}
                      className="absolute top-[9.5px] right-[15px]"
                    >
                      <img src={showConfirmPassword ? Eye : EyeClosed} />
                    </motion.button>
                  )}
                </AnimatePresence>

                <input
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  className="w-full px-[15px] onest-md h-[37px] bg-white/[0.05] text-white rounded-[15px] border-[0.5px] border-white/[0.1]"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                />
              </div>
            </div>

            {/* Display login error if any */}
            {error && <p className="text-red-400 mt-2 text-sm">{error}</p>}
            {message && (
              <p className="text-white leading-[150%] mt-[20px] px-[15px] text-[12px] text-center">
                {message}
              </p>
            )}

            <button className="w-full onest-smb mt-[25px] bg-white text-black h-[38px] flex items-center justify-center rounded-[15px] text-[14px]">
              {!loading && "Change Password"}
              {/* <AnimatePresence> */}
              {loading && (
                <motion.img
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="animate-spin"
                  src={LoadingBlack}
                />
              )}
              {/* </AnimatePresence> */}
            </button>
          </>
        </div>
      </div>
    </form>
  );
};

export default ResetPassword;
